import CampaignApi from "../api/CampaignApi";
import BaseUrl from "../types/BaseUrl";
import IArticleCampaign from "../types/IArticleCampaign";
import IArticleCampaigns from "../types/IArticleCampaigns";
import IArticleCampaignsV2 from "../types/IArticleCampaignsV2";
import IArticleCampaignV2 from "../types/IArticleCampaignV2";
import ICategoryCampaign from "../types/ICategoryCampaign";
import ICategoryCampaigns from "../types/ICategoryCampaigns";
import ICategoryCampaignsV2 from "../types/ICategoryCampaignsV2";
import ICategoryCampaignV2 from "../types/ICategoryCampaignV2";
import ISelectedExtras from "../types/ISelectedExtras";
import Options from "../types/Options";
import CampaignID from "./CampaignID";
import CampaignInfoStorageContainer from "./containers/localStorage";
import CampaignInfoNoStorageContainer from "./containers/noLocalStorageSupport";

export default class CampaignDatasource {
  private readonly merchandisingName = "merchandising";
  private campaignUUID: string;
  private campaignApi: CampaignApi;
  private container: CampaignInfoStorageContainer;
  private options: Options;

  constructor(baseUrl: BaseUrl, options: Options) {
    this.options = options;
    this.campaignUUID = new CampaignID(baseUrl, this.options).getUUID();

    this.campaignApi = new CampaignApi(baseUrl, this.options);
    this.container =
      typeof localStorage !== "undefined" ? new CampaignInfoStorageContainer() : new CampaignInfoNoStorageContainer();

    this.container.empty(this.campaignUUID);
  }

  public async getAllCategoriesCampaignInfo(): Promise<ICategoryCampaigns | ICategoryCampaignsV2> {
    let categories: ICategoryCampaigns | ICategoryCampaignsV2 = this.container.getCategories(this.campaignUUID);

    if (!Object.keys(categories).length) {
      const rawCategories = await this.campaignApi.getAllCategoriesCampaignInfo(this.campaignUUID);

      let merchandisingCategories: ICategoryCampaigns | ICategoryCampaignsV2 = {};
      if (this.merchandisingShouldBeMerged(!!rawCategories.length)) {
        const rawMerchandisingCategories = await this.campaignApi.getAllCategoriesCampaignInfo(this.merchandisingName);
        merchandisingCategories = this.getItemsByIndex(rawMerchandisingCategories, false, (item) =>
          item.product_category.toString()
        );
      }

      categories = {
        ...merchandisingCategories,
        ...this.getItemsByIndex(rawCategories, this.merchandisingShouldBeMerged(!!rawCategories.length), (item) =>
          item.product_category.toString()
        )
      };

      this.container.storeCategories(this.campaignUUID, categories);
    }

    return this.getFinalResult(categories);
  }

  public async getCategoryCampaignInfo(category: string): Promise<ICategoryCampaign | ICategoryCampaignV2> {
    const categoriesInfo = await this.getAllCategoriesCampaignInfo();

    return this.getFinalResult(categoriesInfo[category.toLowerCase()]);
  }

  public async getCategoryArticlesCampaignInfo(category: string): Promise<IArticleCampaigns | IArticleCampaignsV2> {
    let articles: IArticleCampaigns | IArticleCampaignsV2 = this.container.getArticles(this.campaignUUID, category);

    if (!Object.keys(articles).length) {
      const rawArticles = await this.campaignApi.getCategoryArticlesCampaignInfo(this.campaignUUID, category);

      let merchandisingArticles: IArticleCampaigns | IArticleCampaignsV2 = {};
      if (this.merchandisingShouldBeMerged(!!rawArticles.length)) {
        const rawMerchandisingArticles = await this.campaignApi.getCategoryArticlesCampaignInfo(
          this.merchandisingName,
          category
        );
        merchandisingArticles = this.getItemsByIndex(rawMerchandisingArticles, false, (item) =>
          item.product_id.toString()
        );
      }

      articles = {
        ...merchandisingArticles,
        ...this.getItemsByIndex(rawArticles, this.merchandisingShouldBeMerged(!!rawArticles.length), (item) =>
          item.product_id.toString()
        )
      };

      this.container.storeArticles(this.campaignUUID, category, articles);
    }

    return this.getFinalResult(articles);
  }

  public async getArticleCampaignInfo(category: string, papId: string): Promise<IArticleCampaign | IArticleCampaignV2> {
    const articlesInfo = await this.getCategoryArticlesCampaignInfo(category);

    return this.getFinalResult(articlesInfo[papId.toUpperCase()]);
  }

  public async getArticleWithExtrasCampaignInfo(
    category: string,
    papId: string,
    selectedExtras: ISelectedExtras
  ): Promise<IArticleCampaign | IArticleCampaignV2> {
    let articleInfo = await this.campaignApi.getArticleWithExtrasCampaignInfo(
      this.campaignUUID,
      category,
      papId,
      selectedExtras
    );

    if (this.merchandisingShouldBeMerged(!!Object.keys(articleInfo).length)) {
      const merchandisingArticleInfo = await this.campaignApi.getArticleWithExtrasCampaignInfo(
        this.merchandisingName,
        category,
        papId,
        selectedExtras
      );

      if (merchandisingArticleInfo?.discount_price > 0) {
        articleInfo = merchandisingArticleInfo;
      }
    }

    return this.getFinalResult(articleInfo);
  }

  private getFinalResult(items: any): any {
    if (!items || (typeof items === "object" && !Object.keys(items).length)) {
      return {};
    }

    return items;
  }

  getCampaignUUID(): string {
    return this.campaignUUID === this.merchandisingName ? "" : this.campaignUUID;
  }

  private merchandisingShouldBeMerged(isAValidCampaign: boolean = true) {
    return this.getCampaignUUID() !== "" && this.options.merchandisingShouldBeMerged(isAValidCampaign);
  }

  private getItemsByIndex(
    rawItems: any,
    excludeItemsWithoutOffer: boolean,
    getCollectionIndexKey: (item: any) => string
  ) {
    const items: any = [];
    rawItems.forEach((item: any) => {
      if (!excludeItemsWithoutOffer || !!item.discount_price) {
        items[getCollectionIndexKey(item)] = item;

        if (getCollectionIndexKey(item) === "perspex") {
          items["plexiglas"] = JSON.parse(JSON.stringify(item).replace("perspex", "plexiglas"));
        }
      }
    });

    return items;
  }
}
